import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getAccount } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Holds the subscriber record (tier, today's usage, admin flag) for the
 * signed-in user. Fetching /account/me is also what creates the free-tier
 * record and triggers the Sendy sync on first sign-in after signup.
 */
const AccountContext = createContext(null);

export function AccountProvider({ children }) {
  const { isAuthed, user } = useAuth();
  const [account, setAccount] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthed) return null;
    setLoading(true);
    try {
      const a = await getAccount();
      setAccount(a);
      setError(null);
      return a;
    } catch (e) {
      setError(e.message || "Could not load account");
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthed]);

  useEffect(() => {
    if (isAuthed) refresh();
    else setAccount(null);
  }, [isAuthed, user?.id, refresh]);

  const value = useMemo(
    () => ({ account, error, loading, refresh, isAdmin: Boolean(account?.is_admin) }),
    [account, error, loading, refresh],
  );
  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount() {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error("useAccount must be used inside <AccountProvider>");
  return ctx;
}
